import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { InvoiceDetailResponse } from '@/types/invoiceDetail';
import { MabarTransaction } from '@/types/invoice';
import { invoiceService } from '@/services/invoiceService';
import Button from '@/components/ui/Button';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { openExternalUrl } from '@/utils/openExternalUrl';
import { isHttpUrl } from '@/utils/paymentUrl';
import { openReportMailto } from '@/utils/openReportMailto';
import showToast from '@/components/ui/Toast';
import { formatLocalEventTimeRange } from '@/utils/dateUtils';

// Helpers
const formatDateIndonesian = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return dateStr || '-';
  }
};

const formatTimeRange = (startTime?: string, endTime?: string): string => {
  return formatLocalEventTimeRange(startTime, endTime) || '-';
};

const formatRupiah = (amount?: number): string => {
  if (!amount && amount !== 0) return 'Rp. 0';
  return `Rp. ${amount.toLocaleString('id-ID')}`;
};

const ClientTransactionDetailPage: React.FC = () => {
  const { invoice_number, order_number } = useParams<{ invoice_number?: string; order_number?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const invoiceNumber = invoice_number || searchParams.get('invoice_number') || '';
  const orderNumberParam = order_number || searchParams.get('order_number') || '';

  const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetailResponse | null>(null);
  const [mabarTransaction, setMabarTransaction] = useState<MabarTransaction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [downloading, setDownloading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const fetchTransactionData = async () => {
    try {
      setLoading(true);
      setError('');

      if (invoiceNumber) {
        const response = await invoiceService.getInvoiceDetailClient(invoiceNumber);
        setInvoiceDetail(response);
      } else if (orderNumberParam) {
        const res = await invoiceService.getClientMabarTransactions(1, 100, '', 'created_at', 'desc');
        if (res.success && res.data) {
          const match = res.data.find((tx) => tx.order_number === orderNumberParam);
          if (match) {
            setMabarTransaction(match);
          } else {
            setError('Detail transaksi event tidak ditemukan');
          }
        } else {
          setError('Gagal memuat transaksi event');
        }
      } else {
        setError('Nomor transaksi tidak ditemukan');
      }
    } catch (err: any) {
      console.error('Error fetching transaction detail:', err);
      setError(err.message || 'Gagal memuat rincian transaksi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactionData();
  }, [invoiceNumber, orderNumberParam]);

  const handleCancelOrder = async () => {
    const orderNum = mabarTransaction?.order_number || invoiceDetail?.data?.order_number || invoiceNumber || orderNumberParam;
    if (!orderNum) return;
    try {
      setCancelling(true);
      await invoiceService.cancelOrder(orderNum);
      showToast.success('Pesanan berhasil dibatalkan');
      void fetchTransactionData();
    } catch (err: any) {
      console.error('Error cancelling order:', err);
      showToast.error('Gagal membatalkan pesanan');
    } finally {
      setCancelling(false);
      setShowCancelDialog(false);
    }
  };

  const handleCopyOrderNumber = (orderNum: string) => {
    navigator.clipboard.writeText(orderNum);
    showToast.success('No. Order berhasil disalin!');
  };

  const handleDownloadPdf = async (invNum: string) => {
    try {
      setDownloading(true);
      const blob = await invoiceService.downloadClientInvoicePdf(invNum);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invNum}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download invoice PDF:', err);
      showToast.error('Gagal mendownload invoice. Silakan coba lagi.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#F8FAFC] min-h-screen flex flex-col items-center justify-center gap-3">
        <Icon icon="ph:spinner-gap-bold" className="w-8 h-8 text-[#FC480D] animate-spin" />
        <span className="text-xs font-semibold text-gray-400">Memuat rincian transaksi...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#F8FAFC] min-h-screen flex flex-col items-center justify-center p-4 text-center">
        <Icon icon="ph:warning-circle-bold" className="w-12 h-12 text-rose-500 mb-3" />
        <p className="text-sm font-bold text-[#0E1726] mb-1">Gagal Memuat Transaksi</p>
        <p className="text-xs text-gray-400 mb-4">{error}</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-200 text-gray-700 text-xs font-bold rounded-xl hover:bg-gray-300 transition-colors cursor-pointer"
          >
            Kembali
          </button>
          <button
            type="button"
            onClick={fetchTransactionData}
            className="px-4 py-2 bg-[#FC480D] text-white text-xs font-bold rounded-xl hover:bg-[#e03e09] transition-colors cursor-pointer"
          >
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  // Unified properties
  const invoice = invoiceDetail?.data;
  const displayOrderNumber = mabarTransaction?.order_number || invoice?.order_number || invoice?.invoice_number || orderNumberParam || '-';
  const rawStatus = (mabarTransaction?.status || invoice?.status || 'PENDING').toUpperCase();
  const isPaid = rawStatus === 'PAID' || rawStatus === 'LUNAS' || rawStatus === 'SUCCESS' || rawStatus === 'SETTLED';
  const isPending = rawStatus === 'PENDING' || rawStatus === 'UNPAID' || rawStatus === 'WAITING' || rawStatus === 'MENUNGGU PEMBAYARAN';

  const totalAmount = mabarTransaction?.amount ?? invoice?.total_amount ?? invoice?.final_total ?? 0;
  const subtotal = invoice?.subtotal ?? invoice?.item_subtotal ?? totalAmount;
  const serviceFee = invoice?.service_fee ?? invoice?.payment_fee ?? 0;
  const discount = invoice?.discount_amount ?? invoice?.discount ?? 0;

  // Details
  const title = mabarTransaction?.event_title || 'Detail Transaksi';
  const isEvent = Boolean(mabarTransaction) || Boolean(invoice?.order_type?.includes('event') || invoice?.order_type?.includes('mabar'));
  const categoryRole = mabarTransaction
    ? `${mabarTransaction.sport_category || 'Mabar'} · role ${mabarTransaction.role || 'Pemain'}`
    : invoice?.field_bookings?.[0]?.sport_category
    ? `${invoice.field_bookings[0].sport_category} · Sewa Lapangan`
    : 'Booking Lapangan';

  const customerName = mabarTransaction?.customer_name || '-';
  const dateFormatted = mabarTransaction
    ? formatDateIndonesian(mabarTransaction.event_date || mabarTransaction.created_at)
    : invoice
    ? formatDateIndonesian(invoice.booking_date || invoice.created_at)
    : '-';

  const firstFieldBooking = invoice?.field_bookings?.[0];
  const timeFormatted = mabarTransaction
    ? formatTimeRange(mabarTransaction.start_time, mabarTransaction.end_time)
    : firstFieldBooking
    ? formatTimeRange(firstFieldBooking.start_time, firstFieldBooking.end_time)
    : '-';

  const venue = mabarTransaction?.lokasi_arena || firstFieldBooking?.field_name || '-';
  const role = mabarTransaction?.role || (isEvent ? 'Pemain' : null);

  const paymentMethod = mabarTransaction?.payment_method || invoice?.payment_method || 'Pembayaran';
  const paymentUrl = mabarTransaction?.xendit_invoice_url || invoice?.payment_url;
  const hasInvoiceLink = isHttpUrl(paymentUrl);
  const pdfInvoiceNumber = invoice?.invoice_number;

  return (
    <div className="bg-[#F8FAFC] min-h-screen text-[#0E1726] pb-32">
      {/* Top Bar Header (Desktop only, mobile handled by ClientMobileTopbar) */}
      <div className="hidden md:block sticky top-0 z-20 bg-white border-b border-[#E0E6ED] shadow-xs">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-1 -ml-1 rounded-full text-[#0E1726] hover:bg-gray-100 transition-colors active:scale-95 cursor-pointer"
            aria-label="Kembali"
          >
            <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
          </button>
          <h1 className="text-base sm:text-lg font-bold text-[#0E1726] truncate">Detail Transaksi</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-4 md:pt-6 space-y-4 animate-fade-in">
        {/* Status Hero Card */}
        <div className="bg-white rounded-3xl border border-[#E0E6ED] p-6 text-center space-y-2 shadow-2xs">
          {isPaid ? (
            <>
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-500 flex items-center justify-center mx-auto mb-2">
                <Icon icon="ph:check-circle-fill" className="w-8 h-8 text-emerald-500" />
              </div>
              <div className="inline-block px-3 py-0.5 rounded-full bg-emerald-100/70 text-emerald-600 font-extrabold text-[11px] uppercase tracking-wider">
                PAID
              </div>
              <div className="text-3xl font-extrabold text-[#0E1726] tracking-tight">
                {formatRupiah(totalAmount)}
              </div>
              <p className="text-xs text-gray-400 font-medium max-w-xs mx-auto leading-relaxed pt-1">
                Pembayaran sudah terverifikasi. Invoice tersedia untuk diunduh.
              </p>
            </>
          ) : isPending ? (
            <>
              <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-500 flex items-center justify-center mx-auto mb-2">
                <Icon icon="ph:hourglass-medium-bold" className="w-7 h-7 text-amber-500" />
              </div>
              <div className="inline-block px-3 py-0.5 rounded-full bg-amber-100/70 text-amber-600 font-extrabold text-[11px] uppercase tracking-wider">
                {rawStatus === 'UNPAID' ? 'UNPAID' : 'PENDING'}
              </div>
              <div className="text-3xl font-extrabold text-[#0E1726] tracking-tight">
                {formatRupiah(totalAmount)}
              </div>
              <p className="text-xs text-gray-400 font-medium max-w-xs mx-auto leading-relaxed pt-1">
                Menunggu pembayaran kamu. Slot dilepas otomatis bila melewati batas waktu.
              </p>
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center mx-auto mb-2">
                <Icon icon="ph:x-circle-fill" className="w-8 h-8 text-rose-500" />
              </div>
              <div className="inline-block px-3 py-0.5 rounded-full bg-rose-100/70 text-rose-600 font-extrabold text-[11px] uppercase tracking-wider">
                {rawStatus}
              </div>
              <div className="text-3xl font-extrabold text-[#0E1726] tracking-tight">
                {formatRupiah(totalAmount)}
              </div>
              <p className="text-xs text-gray-400 font-medium max-w-xs mx-auto leading-relaxed pt-1">
                Pembayaran tidak diselesaikan sampai batas waktu, slot sudah dilepas.
              </p>
            </>
          )}
        </div>

        {/* Rincian Event / Booking */}
        <div className="space-y-2">
          <h3 className="text-sm font-bold text-[#0E1726] px-1">
            {isEvent ? 'Rincian event' : 'Rincian booking'}
          </h3>
          <div className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs overflow-hidden">
            {/* Header Box */}
            <div className="p-4 border-b border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-50/80 border border-orange-100 text-[#FC480D] flex items-center justify-center shrink-0">
                <Icon
                  icon={isEvent ? 'ph:users-three-fill' : 'ph:calendar-check-fill'}
                  className="w-5 h-5 text-[#FC480D]"
                />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-bold text-[#0E1726] truncate">{title}</h4>
                <p className="text-xs text-gray-400 font-medium truncate">{categoryRole}</p>
              </div>
            </div>

            {/* Info Table */}
            <div className="divide-y divide-gray-100 text-xs">
              <div className="px-4 py-3 flex items-center justify-between gap-3">
                <span className="text-gray-400 font-medium shrink-0">Customer</span>
                <span className="font-bold text-[#0E1726] truncate text-right">{customerName}</span>
              </div>
              <div className="px-4 py-3 flex items-center justify-between gap-3">
                <span className="text-gray-400 font-medium shrink-0">
                  {isEvent ? 'Tanggal event' : 'Tanggal booking'}
                </span>
                <span className="font-bold text-[#0E1726] truncate text-right">{dateFormatted}</span>
              </div>
              <div className="px-4 py-3 flex items-center justify-between gap-3">
                <span className="text-gray-400 font-medium shrink-0">Jam</span>
                <span className="font-bold text-[#0E1726] truncate text-right">{timeFormatted}</span>
              </div>
              <div className="px-4 py-3 flex items-center justify-between gap-3">
                <span className="text-gray-400 font-medium shrink-0">Venue</span>
                <span className="font-bold text-[#0E1726] truncate text-right">{venue}</span>
              </div>
              {role && (
                <div className="px-4 py-3 flex items-center justify-between gap-3">
                  <span className="text-gray-400 font-medium shrink-0">Role</span>
                  <span className="font-bold text-[#0E1726] truncate text-right">{role}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Rincian Biaya */}
        <div className="space-y-2">
          <h3 className="text-sm font-bold text-[#0E1726] px-1">Rincian biaya</h3>
          <div className="bg-white rounded-2xl border border-[#E0E6ED] p-4 shadow-2xs space-y-2.5 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-400 font-medium">
                {isEvent ? 'Biaya peserta' : 'Biaya sewa'}
              </span>
              <span className="font-bold text-[#0E1726]">{formatRupiah(subtotal)}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-gray-400 font-medium">Biaya layanan</span>
              <span className={serviceFee > 0 ? 'font-bold text-[#0E1726]' : 'font-bold text-emerald-600'}>
                {serviceFee > 0 ? formatRupiah(serviceFee) : 'Gratis'}
              </span>
            </div>
            {discount > 0 && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-gray-400 font-medium">Diskon</span>
                <span className="font-bold text-emerald-600">- {formatRupiah(discount)}</span>
              </div>
            )}
            <div className="border-t border-gray-100 pt-2.5 flex items-center justify-between gap-2">
              <span className="font-bold text-[#0E1726]">Total bayar</span>
              <span className="font-extrabold text-[#FC480D] text-base">{formatRupiah(totalAmount)}</span>
            </div>
          </div>
        </div>

        {/* Metode Pembayaran */}
        <div className="space-y-2">
          <h3 className="text-sm font-bold text-[#0E1726] px-1">Metode pembayaran</h3>
          <div className="bg-white rounded-2xl border border-[#E0E6ED] p-3 shadow-2xs flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-900 text-white font-black text-xs flex items-center justify-center shrink-0">
              {paymentMethod.toUpperCase().includes('QR') ? 'QR' : paymentMethod.slice(0, 3).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-xs font-bold text-[#0E1726] truncate">{paymentMethod}</h4>
              <p className="text-[11px] text-gray-400 font-medium truncate mt-0.5">
                {isPaid ? 'Pembayaran terverifikasi' : isPending ? 'Selesaikan pembayaran sebelum tenggat' : 'Pembayaran gagal/kedaluwarsa'}
              </p>
            </div>
          </div>
        </div>

        {/* No. Order & Salin */}
        {displayOrderNumber && displayOrderNumber !== '-' && (
          <div className="bg-white rounded-2xl border border-[#E0E6ED] p-3 shadow-2xs flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <span className="text-[11px] text-gray-400 font-medium block">No. Order</span>
              <span className="text-xs sm:text-sm font-bold text-[#0E1726] truncate block">{displayOrderNumber}</span>
            </div>
            <button
              type="button"
              onClick={() => handleCopyOrderNumber(displayOrderNumber)}
              className="px-3 py-1.5 rounded-xl bg-orange-50 hover:bg-orange-100 text-[#FC480D] font-bold text-xs flex items-center gap-1 transition-colors cursor-pointer shrink-0"
            >
              <Icon icon="ph:copy-bold" className="w-3.5 h-3.5" />
              <span>Salin</span>
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-2.5 pt-2">
          {isPaid && pdfInvoiceNumber && (
            <Button
              type="button"
              onClick={() => handleDownloadPdf(pdfInvoiceNumber)}
              disabled={downloading}
              className="w-full bg-[#FC480D] hover:bg-[#e03e09] text-white font-bold py-3.5 rounded-2xl text-xs sm:text-sm flex items-center justify-center gap-2 cursor-pointer shadow-xs"
            >
              <Icon icon="ph:download-simple-bold" className="w-4 h-4" />
              <span>{downloading ? 'Mengunduh Invoice...' : 'Unduh Invoice (PDF)'}</span>
            </Button>
          )}

          {isPending && (
            <div className="space-y-2">
              {displayOrderNumber && displayOrderNumber !== '-' && (
                <Button
                  type="button"
                  onClick={() => navigate(`/event/checkout/${displayOrderNumber}`)}
                  className="w-full bg-[#FC480D] hover:bg-[#e03e09] text-white font-bold py-3.5 rounded-2xl text-xs sm:text-sm flex items-center justify-center gap-2 cursor-pointer shadow-xs"
                >
                  <Icon icon="ph:qr-code-bold" className="w-4 h-4" />
                  <span>Bayar Sekarang (QRIS)</span>
                </Button>
              )}

              {hasInvoiceLink && paymentUrl && (
                <Button
                  type="button"
                  onClick={() => openExternalUrl(paymentUrl)}
                  className={`w-full font-bold py-3.5 rounded-2xl text-xs sm:text-sm flex items-center justify-center gap-2 cursor-pointer shadow-xs ${
                    displayOrderNumber && displayOrderNumber !== '-'
                      ? 'bg-white border border-[#FC480D] text-[#FC480D] hover:bg-orange-50'
                      : 'bg-[#FC480D] hover:bg-[#e03e09] text-white'
                  }`}
                >
                  <Icon icon="ph:credit-card-bold" className="w-4 h-4" />
                  <span>Bayar via Invoice Link</span>
                </Button>
              )}
            </div>
          )}

          {!isPaid && !isPending && displayOrderNumber && displayOrderNumber !== '-' && (
            <Button
              type="button"
              onClick={() => navigate(`/event/checkout/${displayOrderNumber}`)}
              className="w-full bg-white border border-gray-300 text-[#0E1726] font-bold py-3.5 rounded-2xl text-xs sm:text-sm hover:bg-gray-50 flex items-center justify-center gap-2 transition-colors cursor-pointer"
            >
              <Icon icon="ph:arrows-counter-clockwise-bold" className="w-4 h-4 text-[#FC480D]" />
              <span>Generate Ulang Pembayaran QRIS</span>
            </Button>
          )}

          {isPending && (
            <button
              type="button"
              onClick={() => setShowCancelDialog(true)}
              className="w-full bg-white border border-rose-200 text-rose-600 font-bold py-3 rounded-2xl text-xs sm:text-sm hover:bg-rose-50 transition-colors cursor-pointer"
            >
              Batalkan Pesanan
            </button>
          )}

          <button
            type="button"
            onClick={() =>
              openReportMailto({
                type: 'Transaksi',
                title: `Order #${displayOrderNumber}`,
                url: window.location.href,
              })
            }
            className="w-full bg-white border border-gray-200 text-[#0E1726] font-bold py-3.5 rounded-2xl text-xs sm:text-sm hover:bg-gray-50 flex items-center justify-center gap-2 transition-colors cursor-pointer"
          >
            <Icon icon="ph:headphones-bold" className="w-4 h-4 text-gray-500" />
            <span>Laporkan Masalah Transaksi</span>
          </button>
        </div>
      </div>

      {/* Confirm Cancel Dialog */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        onCancel={() => setShowCancelDialog(false)}
        onConfirm={handleCancelOrder}
        title="Batalkan Pesanan"
        message="Apakah Anda yakin ingin membatalkan pesanan ini? Slot akan dilepas dan tindakan ini tidak dapat dibatalkan."
        confirmText="Ya, Batalkan"
        cancelText="Batal"
        loading={cancelling}
      />
    </div>
  );
};

export default ClientTransactionDetailPage;
